import { Currency, isCurrency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { getOrderById } from "@redotech/redo-customer-portal-app/api";
import { SetAuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import { SetUserContext } from "@redotech/redo-customer-portal-app/contexts/user";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { getExchangeRateAtTimeOfOrder } from "@redotech/redo-customer-portal-shared/util";
import { OrderWithReturnableItems } from "@redotech/redo-model/order";
import { ReturnType, ReturnTypeEnum } from "@redotech/redo-model/return";
import { LineItem } from "@redotech/redo-model/shopify-order";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { BreadcrumbSlot, useBreadcrumb } from "@redotech/redo-web/breadcrumb";
import { CurrencyContext, DEFAULT_CURRENCY } from "@redotech/redo-web/currency";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { createContext, memo, useContext, useState } from "react";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { StorefrontCartContextProvider } from "../contexts/StorefrontCartContext";
import { CurrencyExchangeContext } from "../currency-exchange";
import { hasReturnableItems, makeOrder } from "../utils/utils";
import { Page } from "./page";

// @ts-expect-error: strict-null-checks
export const OrderContext = createContext<OrderWithReturnableItems>(undefined);

export const ReloadOrderContext = createContext<
  ((skipCache?: boolean) => void) | undefined
>(undefined);

export interface OrderProps {
  breadcrumb: BreadcrumbSlot;
}

export const Order = memo(function Order({ breadcrumb }: OrderProps) {
  const { returnType } = useParams() as { returnType: ReturnType };

  const [currency, setCurrency] = useState<Currency>(DEFAULT_CURRENCY);
  const currencyExchange = useContext(CurrencyExchangeContext);
  const rpcClient = useContext(RpcClientContext);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const settings = useContext(SettingsContext);
  const setAuth = useContext(SetAuthContext);
  const setUser = useContext(SetUserContext);

  const reactNavigate = useNavigate();

  const [orderTrigger, setOrderTrigger] = useState<{
    key: symbol;
    skipCache: boolean;
  }>({ key: Symbol(), skipCache: false });

  const orderLoad = useLoad(async () => {
    const [order, exchangeRates] = await Promise.all([
      (async () =>
        makeOrder(
          (await getOrderById(id!, true, orderTrigger.skipCache)).data?.order,
          settings!,
        ))(),
      currencyExchange(),
    ]);

    // if replaceOrderId is true, the id from params could be a mongoose id, shopify order id, shopify order name, etc... so we need to change the url to be the mongoose id without redirecting
    if (searchParams.get("replaceOrderId") !== null) {
      navigate(`/returns-portal/orders/${order._id}`, { replace: true });
    }

    const shopCurrencyStr: string | undefined = order?.shopify?.currency;
    const orderCurrencyStr: string | undefined =
      order?.shopify?.presentment_currency;

    const shopCurrency =
      shopCurrencyStr && isCurrency(shopCurrencyStr)
        ? shopCurrencyStr
        : DEFAULT_CURRENCY;
    const orderPresentmentCurrency =
      orderCurrencyStr && isCurrency(orderCurrencyStr)
        ? orderCurrencyStr
        : DEFAULT_CURRENCY;

    let formatCurrency: (amount: number) => string;
    if (orderPresentmentCurrency !== shopCurrency) {
      try {
        const exchangeRate =
          getExchangeRateAtTimeOfOrder(
            order?.shopify?.line_items as LineItem[],
          ) ??
          // Fallback to live rates if historical rate not available
          exchangeRates.exchangeRate(shopCurrency, orderPresentmentCurrency);
        formatCurrency = (amount) =>
          moneyFormat({
            amount: amount * exchangeRate,
            currency: orderPresentmentCurrency,
          });
      } catch (e) {
        console.error(e);
        throw e;
      }
    }
    // @ts-expect-error: strict-null-checks
    if (formatCurrency === undefined) {
      formatCurrency = (amount: number) =>
        moneyFormat({ amount, currency: shopCurrency });
    }

    const formatCurrencyFromUSD = (amount: number) => {
      return moneyFormat(
        exchangeRates.convert(
          { amount, currency: Currency.USD },
          orderPresentmentCurrency,
        ),
      );
    };

    return {
      order,
      orderCurrency: orderPresentmentCurrency,
      formatCurrency,
      formatCurrencyFromUSD,
    };
  }, [orderTrigger]);

  const reloadTeam = useHandler((skipCache = false) =>
    setOrderTrigger({ key: Symbol(), skipCache }),
  );

  // Set breadcrumb based on order load state
  useBreadcrumb(
    breadcrumb,
    orderLoad.value?.order?.shopify?.name
      ? `Order ${orderLoad.value.order.shopify.name}`
      : "",
  );

  // Loads the returnable items and adds them to the order. Reloads anytime the return type (i.e. claim or return) changes.
  //  For bundles the returnable items can look slightly different based on return type so we need to reload them.
  const returnableOrderLoad = useLoad(async () => {
    if (!orderLoad.value) {
      return undefined;
    }
    const { order } = orderLoad.value;

    order.hasReturnableItems = await hasReturnableItems(
      order,
      (returnType as ReturnTypeEnum) || ReturnTypeEnum.RETURN,
      rpcClient,
    );
    return order;
  }, [orderLoad.value, returnType, rpcClient]);

  if (orderLoad.error) {
    console.error(orderLoad.error);
  }

  if (!orderLoad.pending && orderLoad.error) {
    if ((orderLoad.error as any)?.status === 401) {
      return (
        <Flex align="center" flexDirection="column" gap="md" m="xl">
          <Text textAlign="center">
            Your login credentials were changed, please click the link below to
            re-login again.
          </Text>
          <RedoButton
            hierarchy="link_underline"
            onClick={() => {
              setAuth?.(undefined);
              setUser?.(undefined);
              reactNavigate("/returns-portal/login");
            }}
            text="Go to login"
          />
        </Flex>
      );
    }
    return (
      <Page>
        <div>There was an error loading this order</div>
      </Page>
    );
  }

  if (
    orderLoad.pending ||
    returnableOrderLoad.pending ||
    !returnableOrderLoad.value
  ) {
    return (
      <Page>
        <LoadingRedoAnimation />
      </Page>
    );
  }

  // @ts-expect-error: strict-null-checks
  const { order, orderCurrency, formatCurrency, formatCurrencyFromUSD } =
    orderLoad.value;
  if (currency !== orderCurrency) {
    // TODO: Can't setState here
    setCurrency(orderCurrency);
  }

  return (
    <OrderContext.Provider value={returnableOrderLoad.value}>
      <ReloadOrderContext.Provider value={reloadTeam}>
        <CurrencyContext.Provider
          value={{
            currency: orderCurrency,
            formatCurrency,
            setCurrency,
            formatCurrencyFromUSD,
          }}
        >
          <StorefrontCartContextProvider>
            <Page>
              <Outlet />
            </Page>
          </StorefrontCartContextProvider>
        </CurrencyContext.Provider>
      </ReloadOrderContext.Provider>
    </OrderContext.Provider>
  );
});
